'use client'

import * as React from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

/**
 * "This control is off, and here is why."
 *
 * Extracted because it was already byte-alike in two places —
 * `components/announcements/editable-date-cell.tsx` (props at :27, branch at
 * :89-97) and `components/phishing/editable-date-cell.tsx` (identical lines) —
 * and the AI degradation work needed it on Generate buttons, tone selects and
 * regenerate actions across ~40 surfaces. That would have been the fourth,
 * fifth and fortieth fork of the same fifteen lines.
 *
 * ── What this fixes versus the two forks ────────────────────────────────────
 * Both forks put the reason in a Tooltip and NOWHERE else. A tooltip fires on
 * hover and on focus — but a disabled control is not focusable, so on those
 * cells the reason was unreachable by keyboard and invisible to a screen
 * reader. Here the reason is also a `title` attribute and a visually-hidden
 * span inside the DOM, so it is readable without a pointer. The tooltip stays
 * for sighted mouse users; it is no longer the only channel.
 *
 * Three branches, matching the originals:
 *   - not disabled            → children, untouched, zero wrapper
 *   - disabled, no reason     → children, untouched (nothing useful to say)
 *   - disabled, with a reason → wrapped, announced, tooltipped
 *
 * The `reason` is caller-supplied copy. For AI surfaces it must come from
 * `lib/ai/quota-copy.ts` (e.g. `AI_DISABLED_REASON.exhausted`) — never a raw
 * server message, which is how provider text leaks into the UI.
 */
export interface DisabledWithReasonProps {
  disabled: boolean
  /** Plain sentence. Omit when there is nothing meaningful to explain. */
  reason?: string | null
  children: React.ReactNode
  className?: string
  /** Tooltip side; matches the editable-date-cell default. */
  side?: 'top' | 'right' | 'bottom' | 'left'
}

export function DisabledWithReason({
  disabled,
  reason,
  children,
  className,
  side = 'top',
}: DisabledWithReasonProps) {
  const trimmed = typeof reason === 'string' ? reason.trim() : ''

  if (!disabled || trimmed.length === 0) {
    return <>{children}</>
  }

  const content = (
    <span
      data-testid="disabled-with-reason"
      data-disabled="true"
      title={trimmed}
      className={cn('inline-flex cursor-not-allowed items-center', className)}
    >
      {children}
      {/* The reason, reachable without a pointer. The tooltip below is an
          enhancement for sighted mouse users, not the only way to read it. */}
      <span className="sr-only">{trimmed}</span>
    </span>
  )

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side={side} className="max-w-[260px]">
          <p className="text-xs">{trimmed}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
